/**
 * Sample one-file proxy server code for forwarding events.
 *
 * Each proxy server:
 * 1. Accepts POST with ?target=<url> query parameter
 * 2. Forwards the request body to the target URL
 * 3. Returns the target's response (status + body) to the caller
 * 4. Includes CORS headers for browser compatibility
 * 5. Exposes GET /health for liveness checks
 */

export interface SampleServer {
  language: string;
  label: string;
  filename: string;
  command: string;
  code: string;
}

/** Build sample servers with the caller's origin for CORS headers. */
export const getSampleServers = (origin: string): SampleServer[] => [
  {
    language: 'python',
    label: 'Python',
    filename: 'proxy.py',
    command: 'python3 proxy.py',
    code: `"""
Lightweight proxy server for forwarding Chargebee event payloads.

Usage:
  python3 proxy.py

The browser sends event payloads to this proxy, which forwards them
to your target server. This avoids CORS issues since browser-to-server
requests would be blocked without CORS headers on the target.

Query parameters (passed by the browser when forwarding):
  ?target=<url>  - Required. The target server URL to forward to.

Basic Auth:
  Include credentials in the target URL using standard URL format:
    https://user:password@host/path
  The proxy extracts the credentials, adds a Basic Auth header,
  and forwards to the URL without the userinfo component.

Endpoints:
  POST /?target=<url>    - Forward the request body to the target URL.
  GET  /health           - Health check (returns {"status":"ok"}).
  OPTIONS /*             - CORS preflight response.

Example:
  python3 proxy.py
  # Proxy listens on http://localhost:8000
  # Browser calls: POST http://localhost:8000?target=http://localhost:3000/webhooks
  # With auth:     POST http://localhost:8000?target=https://site:pass@app.com/webhooks
"""

from http.server import HTTPServer, BaseHTTPRequestHandler
from urllib.parse import urlparse, parse_qs, urlunparse
import json, base64, urllib.request


class ProxyHandler(BaseHTTPRequestHandler):

    # ── CORS headers ──────────────────────────────────────────────────
    # Allow requests only from the Chargebee API Explorer origin.
    def _cors(self):
        self.send_header("Access-Control-Allow-Origin", "${origin}")
        self.send_header("Access-Control-Allow-Methods", "GET, POST, OPTIONS")
        self.send_header("Access-Control-Allow-Headers", "Content-Type")

    # ── CORS preflight ────────────────────────────────────────────────
    # Browsers send an OPTIONS request before the actual POST.
    def do_OPTIONS(self):
        self.send_response(204)
        self._cors()
        self.end_headers()

    # ── Health check ──────────────────────────────────────────────────
    # Used by the API Explorer to show proxy connection status (green/red dot).
    def do_GET(self):
        if self.path.startswith("/health"):
            self.send_response(200)
            self._cors()
            self.send_header("Content-Type", "application/json")
            self.end_headers()
            self.wfile.write(b'{"status":"ok"}')
            return

    # ── Forward event payload ─────────────────────────────────────────
    def do_POST(self):
        # Parse the ?target=<url> query parameter
        qs = parse_qs(urlparse(self.path).query)
        target = qs.get("target", [None])[0]

        if not target:
            self.send_response(400)
            self._cors()
            self.send_header("Content-Type", "application/json")
            self.end_headers()
            self.wfile.write(b'{"error":"Missing ?target= query parameter"}')
            return

        # Extract Basic Auth credentials from target URL if present
        # e.g. https://user:password@host/path -> user:password
        parsed_target = urlparse(target)
        auth = None
        if parsed_target.username:
            auth = parsed_target.username
            if parsed_target.password:
                auth += ":" + parsed_target.password
            # Rebuild target URL without credentials
            target = urlunparse(parsed_target._replace(
                netloc=parsed_target.hostname + ((":" + str(parsed_target.port)) if parsed_target.port else "")
            ))

        # Read the event payload from the request body
        length = int(self.headers.get("Content-Length", 0))
        body = self.rfile.read(length)

        try:
            # Build headers for the forwarded request
            fwd_headers = {"Content-Type": "application/json"}
            if auth:
                # Add Basic Auth header: base64-encode "user:password"
                fwd_headers["Authorization"] = "Basic " + base64.b64encode(auth.encode()).decode()

            # Forward the payload to the target server
            req = urllib.request.Request(
                target, data=body,
                headers=fwd_headers,
                method="POST",
            )
            with urllib.request.urlopen(req, timeout=30) as resp:
                resp_body = resp.read()
                # Return the target's response back to the browser
                self.send_response(resp.status)
                self._cors()
                self.send_header("Content-Type", resp.getheader("Content-Type", "application/json"))
                self.end_headers()
                self.wfile.write(resp_body)

        except urllib.error.HTTPError as e:
            # Target returned an HTTP error — pass it through
            self.send_response(e.code)
            self._cors()
            self.send_header("Content-Type", "application/json")
            self.end_headers()
            self.wfile.write(e.read())
        except Exception as e:
            # Network error or target unreachable
            self.send_response(502)
            self._cors()
            self.send_header("Content-Type", "application/json")
            self.end_headers()
            self.wfile.write(json.dumps({"error": str(e)}).encode())

    # ── Custom log format ─────────────────────────────────────────────
    def log_message(self, fmt, *args):
        target = parse_qs(urlparse(self.path).query).get("target", [""])[0]
        print(f"[proxy] {args[0]} -> {target}")


print("Proxy listening on http://localhost:8000")
HTTPServer(("", 8000), ProxyHandler).serve_forever()`,
  },
  {
    language: 'javascript',
    label: 'Node.js',
    filename: 'proxy.js',
    command: 'node proxy.js',
    code: `/**
 * Lightweight proxy server for forwarding Chargebee event payloads.
 *
 * Usage:
 *   node proxy.js
 *
 * The browser sends event payloads to this proxy, which forwards them
 * to your target server. This avoids CORS issues since browser-to-server
 * requests would be blocked without CORS headers on the target.
 *
 * Query parameters (passed by the browser when forwarding):
 *   ?target=<url>  - Required. The target server URL to forward to.
 *
 * Basic Auth:
 *   Include credentials in the target URL using standard URL format:
 *     https://user:password@host/path
 *   The proxy extracts the credentials, adds a Basic Auth header,
 *   and forwards to the URL without the userinfo component.
 *
 * Endpoints:
 *   POST /?target=<url>    - Forward the request body to the target URL.
 *   GET  /health           - Health check (returns {"status":"ok"}).
 *   OPTIONS /*             - CORS preflight response.
 *
 * Example:
 *   node proxy.js
 *   // Proxy listens on http://localhost:8000
 *   // Browser calls: POST http://localhost:8000?target=http://localhost:3000/webhooks
 *   // With auth:     POST http://localhost:8000?target=https://site:pass@app.com/webhooks
 */

const http = require("http");

http
  .createServer((req, res) => {
    // ── CORS headers ──────────────────────────────────────────────────
    // Allow requests only from the Chargebee API Explorer origin.
    res.setHeader("Access-Control-Allow-Origin", "${origin}");
    res.setHeader("Access-Control-Allow-Methods", "GET, POST, OPTIONS");
    res.setHeader("Access-Control-Allow-Headers", "Content-Type");

    // ── CORS preflight ────────────────────────────────────────────────
    // Browsers send an OPTIONS request before the actual POST.
    if (req.method === "OPTIONS") {
      res.writeHead(204);
      return res.end();
    }

    const url = new URL(req.url, "http://localhost");

    // ── Health check ──────────────────────────────────────────────────
    // Used by the API Explorer to show proxy connection status (green/red dot).
    if (req.method === "GET" && url.pathname === "/health") {
      res.writeHead(200, { "Content-Type": "application/json" });
      return res.end('{"status":"ok"}');
    }

    // ── Parse the ?target=<url> query parameter ───────────────────────
    const target = url.searchParams.get("target");
    if (!target) {
      res.writeHead(400, { "Content-Type": "application/json" });
      return res.end('{"error":"Missing ?target= query parameter"}');
    }

    // ── Read the event payload from the request body ──────────────────
    let body = "";
    req.on("data", (c) => (body += c));
    req.on("end", () => {
      const dest = new URL(target);

      // Extract Basic Auth credentials from target URL if present
      // e.g. https://user:password@host/path -> user:password
      const auth = dest.username
        ? dest.username + (dest.password ? ":" + dest.password : "")
        : null;
      // Remove credentials from the URL before forwarding
      dest.username = "";
      dest.password = "";

      // Use https module if target is HTTPS, otherwise use http
      const mod = dest.protocol === "https:" ? require("https") : http;

      // ── Forward the payload to the target server ────────────────────
      const proxy = mod.request(
        {
          hostname: dest.hostname,
          port: dest.port,
          path: dest.pathname + dest.search,
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Content-Length": Buffer.byteLength(body),
            // Add Basic Auth header if credentials were in the target URL
            ...(auth && { Authorization: "Basic " + Buffer.from(auth).toString("base64") }),
          },
        },
        (upstream) => {
          // ── Return the target's response back to the browser ────────
          let resp = "";
          upstream.on("data", (c) => (resp += c));
          upstream.on("end", () => {
            console.log("[proxy]", upstream.statusCode, "->", dest.href);
            res.writeHead(upstream.statusCode, {
              "Content-Type": upstream.headers["content-type"] || "application/json",
            });
            res.end(resp);
          });
        }
      );

      // ── Network error or target unreachable ─────────────────────────
      proxy.on("error", (err) => {
        res.writeHead(502, { "Content-Type": "application/json" });
        res.end(JSON.stringify({ error: err.message }));
      });

      proxy.end(body);
    });
  })
  .listen(8000, () => console.log("Proxy listening on http://localhost:8000"));`,
  },
];

// ─── Forward Mode Constants ─────────────────────────────────────────────────

export type ForwardMode = 'proxy' | 'direct';

export const FORWARD_MODE_KEY = 'cb-forward-mode';
export const PROXY_URL_KEY = 'cb-forward-proxy-url';
export const TARGET_URL_KEY = 'cb-forward-target-url';
export const LEGACY_URL_KEY = 'cb-event-forward-url';
export const AUTO_FORWARD_ENABLED_KEY = 'cb-auto-forward-enabled';
